<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { get } from 'lodash-es';
import { BFormSelect } from 'bootstrap-vue-next';
import type { IColumnDefinition, RowData, ISelectColumn } from '../types';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

const props = defineProps<{
  modelValue: any;
  row: RowData;
  column: IColumnDefinition;
  autofocus?: boolean;
}>();
const emit = defineEmits(['update:modelValue', 'change']);

const col = computed(() => props.column as ISelectColumn);
const valField = col.value.valueField || 'id';
const txtField = col.value.textField || 'text';

const isSmartMode = computed(() => {
  return (
    col.value.type === 'select2' || typeof col.value.options === 'function'
  );
});

const asyncOptions = ref<any[]>([]);
const isLoading = ref(false);

const currentOptions = computed(() => {
  if (Array.isArray(col.value.options)) {
    return col.value.options;
  }
  return asyncOptions.value;
});

const onSearch = async (query: string, loading: (l: boolean) => void) => {
  if (typeof col.value.options === 'function') {
    loading(true);
    isLoading.value = true;
    try {
      const results = await col.value.options(query);
      asyncOptions.value = results || [];
    } catch (e) {
      console.error('Select2 search error', e);
      asyncOptions.value = [];
    } finally {
      loading(false);
      isLoading.value = false;
    }
  }
};

const normalizedOptions = computed(() => {
  const opts = currentOptions.value;

  return opts.map((opt: any) => {
    if (typeof opt === 'string') {
      return { value: opt, text: opt, raw: opt };
    }
    return {
      value: col.value.bindObject ? opt : get(opt, valField),
      text: get(opt, txtField),
      raw: opt,
    };
  });
});

const currentValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const nativeInput = ref<any>(null);
const smartInput = ref<any>(null);

onMounted(() => {
  if (props.autofocus) {
    if (nativeInput.value) {
      const el = nativeInput.value;
      if (el.element && el.element.focus) {
        el.element.focus();
      } else if (el.focus) {
        el.focus();
      }
    }
    if (smartInput.value?.searchEl) {
      smartInput.value.searchEl.focus();
    }
  }
});

const onChange = () => {
  emit('change', currentValue.value);
};

const reduceSmartValue = (option: any) => {
  if (col.value.bindObject) {
    return option;
  }
  return get(option, valField);
};
</script>

<template>
  <div v-if="isSmartMode" class="itp-smart-select-wrapper">
    <vSelect
      ref="smartInput"
      v-model="currentValue"
      :options="currentOptions"
      :label="txtField"
      :reduce="reduceSmartValue"
      :clearable="!column.required"
      :searchable="true"
      :placeholder="col.placeholder"
      :disabled="col.editorIsDisabled ? col.editorIsDisabled(row) : false"
      class="itp-smart-select"
      append-to-body
      @search="onSearch"
      @option:selected="onChange"
    >
      <template #no-options="{ search, searching }">
        <template v-if="searching"
          >No result for: <em>{{ search }}</em></template
        >
        <template v-else>Type something to search...</template>
      </template>
    </vSelect>
  </div>

  <BFormSelect
    v-else
    ref="nativeInput"
    v-model="currentValue"
    size="sm"
    :options="normalizedOptions"
    :disabled="col.editorIsDisabled ? col.editorIsDisabled(row) : false"
    @change="onChange"
    @click.stop
  />
</template>

<style lang="scss">
.itp-smart-select-wrapper {
  min-width: 140px;

  .vs__dropdown-toggle {
    padding: 0 0 4px 0;
    border: var(--bs-border-width) solid var(--bs-border-color);
    border-radius: var(--bs-border-radius-sm);
    min-height: 31px;
    background: var(--bs-body-bg);
  }
  .vs__search,
  .vs__selected {
    margin: 2px 4px;
    padding: 0;
    font-size: 0.875rem;
    line-height: 1.5;
  }
  .vs__actions {
    padding: 0 4px 0 0;
  }
}
</style>
